import React, { useState, useEffect } from 'react';
import { Select, Tag as AntTag, Button, Space, message } from 'antd';
import { PlusOutlined, TagsOutlined } from '@ant-design/icons';
import { tagsApi } from '../../api/tags/tags.api';
import { Tag } from '../../common/tag.interface';
import './TagsSelector.styles.scss';

interface TagsSelectorProps {
  value?: number[];
  onChange?: (value: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
  bookId?: number; // For loading existing tags when editing
}

const TagsSelector: React.FC<TagsSelectorProps> = ({
  value = [],
  onChange,
  placeholder = "Chọn tags cho sách",
  disabled = false,
  bookId
}) => {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    if (bookId) {
      loadBookTags();
    }
  }, [bookId]);

  const loadTags = async () => {
    try {
      setLoading(true);
      const tags = await tagsApi.getActiveTags();
      setAllTags(tags);
    } catch (error) {
      message.error('Không thể tải danh sách tags');
      console.error('Error loading tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBookTags = async () => {
    if (!bookId) return;
    
    try {
      const bookTags = await tagsApi.getTagsByBook(bookId);
      const tagIds = bookTags.map(tag => tag.id);
      onChange?.(tagIds);
    } catch (error) {
      console.error('Error loading book tags:', error);
    }
  };

  const handleTagChange = (selectedIds: number[]) => {
    onChange?.(selectedIds);
  };

  const getSelectedTags = () => {
    return allTags.filter(tag => value.includes(tag.id));
  };

  const renderTagOption = (tag: Tag) => (
    <Select.Option key={tag.id} value={tag.id}>
      <Space>
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: tag.color || '#ccc',
            border: '1px solid #d9d9d9'
          }}
        />
        <span>{tag.name}</span>
        {tag.description && (
          <span style={{ color: '#999', fontSize: '12px' }}>
            ({tag.description})
          </span>
        )}
      </Space>
    </Select.Option>
  );

  const renderSelectedTags = () => {
    const selectedTags = getSelectedTags();
    
    if (selectedTags.length === 0) {
      return null;
    }

    return (
      <div className="selected-tags-display">
        <Space wrap size={[4, 4]}>
          {selectedTags.map(tag => (
            <AntTag
              key={tag.id}
              color={tag.color}
              closable={!disabled}
              onClose={() => {
                const newValue = value.filter(id => id !== tag.id);
                handleTagChange(newValue);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                margin: '2px',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px'
              }}
            >
              <TagsOutlined style={{ fontSize: '10px' }} />
              {tag.name}
            </AntTag>
          ))}
        </Space>
      </div>
    );
  };

  return (
    <div className="tags-selector">
      <Select
        mode="multiple"
        placeholder={placeholder}
        value={value}
        onChange={handleTagChange}
        loading={loading}
        disabled={disabled}
        style={{ width: '100%' }}
        maxTagCount="responsive"
        optionFilterProp="children"
        showSearch
        filterOption={(input, option) => {
          const tag = allTags.find(t => t.id === option?.value);
          if (!tag) return false;
          
          const searchText = input.toLowerCase();
          return Boolean(
            tag.name.toLowerCase().includes(searchText) ||
            (tag.description && tag.description.toLowerCase().includes(searchText))
          );
        }}
        dropdownRender={(menu) => (
          <div>
            {menu}
            <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
              <Button
                type="text"
                icon={<PlusOutlined />}
                size="small"
                style={{ width: '100%' }}
                onClick={() => {
                  message.info('Vui lòng vào trang "Quản lý Tags" để thêm tag mới');
                }}
              >
                Thêm tag mới
              </Button>
            </div>
          </div>
        )}
      >
        {allTags.map(renderTagOption)}
      </Select>
      
      {renderSelectedTags()}
    </div>
  );
};

export default TagsSelector;
